import * as THREE from 'three';
import type { SfxName } from '../content/types';
import type { Controls } from '../core/device';
import type { NpcAgent } from '../entities/NpcManager';
import type { Player } from '../entities/Player';
import type { DartsGame } from '../minigames/darts';
import type { MiniInput } from '../minigames/types';
import type { Vec2 } from '../world/layout';
import { cyl, mat, mesh } from '../world/materials';
import { DARTBOARD_SCORING } from '../world/textures';
import type { Stage, StageButton, StageHud, StageInput } from './types';

/** A dart, built along +z with the point forward. */
function buildDart(flight: string): THREE.Group {
  const g = new THREE.Group();
  const steel = mat('#c9ccd1', { metal: 0.8, rough: 0.25 });
  g.add(cyl(0.0, 0.004, 0.035, steel, 0, 0, 0.0525, 6).rotateX(Math.PI / 2));
  g.add(
    cyl(0.006, 0.006, 0.05, mat('#7a7f86', { metal: 0.7, rough: 0.35 }), 0, 0, 0.012, 8).rotateX(Math.PI / 2),
  );
  g.add(cyl(0.0025, 0.0025, 0.04, mat('#222'), 0, 0, -0.033, 4).rotateX(Math.PI / 2));
  const fm = mat(flight, { rough: 0.6 });
  fm.side = THREE.DoubleSide;
  for (const r of [0, Math.PI / 2]) {
    const f = mesh(new THREE.PlaneGeometry(0.028, 0.035), fm, 0, 0, -0.06, { cast: false });
    f.rotation.set(0, Math.PI / 2, r);
    g.add(f);
  }
  return g;
}

export interface DartsSetup {
  /** The dartboard: faces +x in its own space, and the chalkboard beside it. */
  board: THREE.Group;
  chalk: THREE.Mesh;
  /** The board face radius (m); the scoring rings fill DARTBOARD_SCORING of it. */
  radius: number;
  /** Where you stand to throw (world), and which way the board is (facing angle). */
  oche: Vec2;
  facing: number;
  /** Your opponent, if she's around to stand next to you. */
  rival: NpcAgent | null;
  rivalSpot: Vec2;
}

interface Flying {
  mesh: THREE.Group;
  from: THREE.Vector3;
  to: THREE.Vector3;
  t: number;
}

/**
 * Darts at the Rusty Spur, at the real board on the wall. You're at the line; the aim
 * drifts across the board (more after a few beers) and you let fly when it's lined up.
 */
export class DartsStage implements Stage {
  readonly title: string;
  readonly crosshair = false;
  readonly pointerLock = false;
  readonly game: DartsGame;
  private t = 0;
  private seen = 0;
  private readonly stuck: THREE.Group[] = [];
  private readonly flying: Flying[] = [];
  private readonly reticle: THREE.Mesh;
  private readonly chalkCanvas: HTMLCanvasElement;
  private readonly chalkTex: THREE.CanvasTexture;
  private readonly chalkOld: THREE.Material | THREE.Material[];
  private chalkKey = '';
  private readonly eye = new THREE.Vector3();
  private readonly center = new THREE.Vector3();
  private readonly tmp = new THREE.Vector3();
  private baseFov = 60;
  private camera: THREE.PerspectiveCamera | null = null;
  private lastRound = 1;
  /** The dart in your hand (first person), and when you last let one go. */
  private readonly inHand = buildDart('#ffd400');
  private throwAt = -9;

  constructor(
    private readonly setup: DartsSetup,
    private readonly player: Player,
    private readonly sfx: (n: SfxName) => void,
    game: DartsGame,
    /** First-person props root (follows the camera). */
    private readonly view: THREE.Object3D,
  ) {
    this.game = game;
    this.title = game.title;
    this.reticle = mesh(
      new THREE.RingGeometry(0.011, 0.018, 20),
      new THREE.MeshBasicMaterial({ color: '#ffffff', depthTest: false, transparent: true }),
      0,
      0,
      0.04,
      { cast: false },
    );
    this.reticle.renderOrder = 5;
    this.chalkCanvas = document.createElement('canvas');
    this.chalkCanvas.width = 384;
    this.chalkCanvas.height = 320;
    this.chalkTex = new THREE.CanvasTexture(this.chalkCanvas);
    this.chalkTex.colorSpace = THREE.SRGBColorSpace;
    this.chalkOld = setup.chalk.material;
  }

  get done(): boolean {
    return this.game.done && this.flying.length === 0;
  }

  hint(controls: Controls): string {
    if (this.game.phase === 'them') return `${this.game.opponent.split(' ')[0]}'s throwing...`;
    return this.game.hint(controls);
  }

  buttons(touch: boolean): StageButton[] {
    return touch ? [{ code: 'Space', label: ':target:', primary: true }] : [];
  }

  begin(): void {
    const { setup, player } = this;
    player.teleport(setup.oche.x, setup.oche.z, setup.facing);
    player.scripted = true;
    player.viewHidden = true;
    player.anim.update({ dt: 1, time: 0, speed: 0, pose: 'throw' });
    setup.board.add(this.reticle);
    this.inHand.scale.setScalar(1.4);
    this.view.add(this.inHand);
    setup.chalk.material = new THREE.MeshStandardMaterial({ map: this.chalkTex, roughness: 1 });
    if (setup.rival)
      setup.rival.hold = {
        x: setup.rivalSpot.x,
        z: setup.rivalSpot.z,
        heading: setup.facing,
        pose: 'stand',
      };
  }

  /** A point on the board face (aim/dart coordinates are in board radii, y down) in world space. */
  private boardPoint(x: number, y: number, out: THREE.Vector3, lift = 0.035): THREE.Vector3 {
    const r = this.setup.radius * DARTBOARD_SCORING;
    // The board's face is its local +z; looking at it, local +x is to your right.
    out.set(x * r, -y * r, lift);
    return this.setup.board.localToWorld(out);
  }

  update(dt: number, input: StageInput): void {
    this.t += dt;
    const g = this.game;
    const mini: MiniInput = {
      held: input.held,
      pressed: input.pressed || (input.fire && !input.pressed),
      released: input.released,
      tap: null,
      keys: input.keys,
    };
    // Don't let a throw go while the last dart is still in the air.
    if (this.flying.length) mini.pressed = false;
    g.update(dt, mini);
    const rival = this.setup.rival;
    if (rival?.hold) rival.hold.pose = g.phase === 'them' ? 'throw' : 'stand';
    this.player.anim.update({ dt, time: this.t, speed: 0, pose: g.phase === 'you' ? 'throw' : 'stand' });

    // New darts fly in from whoever threw them.
    while (this.seen < g.history.length) {
      const d = g.history[this.seen++];
      if (!d) continue;
      const dart = buildDart(d.who === 'you' ? '#ffd400' : '#5ff3ff');
      dart.scale.setScalar(1.7);
      if (d.who === 'you') this.throwAt = this.t;
      const from =
        d.who === 'you'
          ? this.player.eye(new THREE.Vector3()).add(this.tmp.set(0, -0.12, 0))
          : (rival?.rig.handR.getWorldPosition(new THREE.Vector3()) ?? this.player.eye(new THREE.Vector3()));
      const to = this.boardPoint(d.x, d.y, new THREE.Vector3(), 0.05);
      dart.position.copy(from);
      this.setup.board.parent?.add(dart);
      this.flying.push({ mesh: dart, from, to, t: 0 });
      this.sfx('whoop');
    }
    for (let i = this.flying.length - 1; i >= 0; i--) {
      const f = this.flying[i] as Flying;
      f.t += dt / 0.24;
      const k = Math.min(1, f.t);
      const p = this.tmp.lerpVectors(f.from, f.to, k);
      p.y += Math.sin(k * Math.PI) * 0.12;
      const parent = f.mesh.parent;
      if (parent) {
        parent.worldToLocal(p);
        f.mesh.position.copy(p);
        f.mesh.lookAt(f.to);
      }
      if (k >= 1) {
        this.flying.splice(i, 1);
        // Stick it in the board: re-home it as a child of the board so it stays put.
        const local = this.setup.board.worldToLocal(f.to.clone());
        this.setup.board.add(f.mesh);
        f.mesh.position.copy(local).setZ(0.05 + 0.03);
        f.mesh.rotation.set(Math.PI + (Math.random() - 0.5) * 0.25, (Math.random() - 0.5) * 0.25, 0);
        this.stuck.push(f.mesh);
        this.sfx('thunk');
      }
    }
    // A fresh round pulls the darts out of the board.
    if (g.round !== this.lastRound && this.flying.length === 0) {
      this.lastRound = g.round;
      this.clearDarts();
    }
    // The aim: a ring drifting across the board while it's your throw.
    const aim = g.aimAt();
    this.reticle.visible = g.phase === 'you' && this.flying.length === 0;
    const r = this.setup.radius * DARTBOARD_SCORING;
    this.reticle.position.set(aim.x * r, -aim.y * r, 0.04);
    this.drawChalk();
  }

  private clearDarts(): void {
    for (const d of this.stuck) {
      d.removeFromParent();
      d.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
    }
    this.stuck.length = 0;
  }

  /** Scores in chalk next to the board. */
  private drawChalk(): void {
    const g = this.game;
    const key = `${g.round}|${g.score.you}|${g.score.them}|${g.phase}|${g.thrown}`;
    if (key === this.chalkKey) return;
    this.chalkKey = key;
    const c = this.chalkCanvas.getContext('2d');
    if (!c) return;
    c.fillStyle = '#1e2b22';
    c.fillRect(0, 0, 384, 320);
    c.fillStyle = 'rgba(255,255,255,0.08)';
    for (let i = 0; i < 30; i++) c.fillRect(Math.random() * 384, Math.random() * 320, 40, 3);
    c.fillStyle = '#f2efe6';
    c.textAlign = 'center';
    c.font = '40px "Permanent Marker", cursive';
    c.fillText(`ROUND ${Math.min(3, g.round)}/3`, 192, 62);
    c.fillStyle = '#ffd400';
    c.font = '52px "Permanent Marker", cursive';
    c.fillText(`YOU ${g.score.you}`, 192, 150);
    c.fillStyle = '#5ff3ff';
    c.fillText(`${g.opponent.split(' ')[0]?.toUpperCase()} ${g.score.them}`, 192, 230);
    c.fillStyle = '#f2efe6';
    c.font = '30px "Permanent Marker", cursive';
    c.fillText(
      g.phase === 'you' ? `YOUR DART ${g.thrown + 1}/3` : g.phase === 'them' ? 'HER THROW' : '',
      192,
      292,
    );
    this.chalkTex.needsUpdate = true;
  }

  frame(camera: THREE.PerspectiveCamera, dt: number): void {
    if (this.camera !== camera) {
      this.camera = camera;
      this.baseFov = camera.fov;
    }
    // From the line, zoomed in on the board (and drifting a touch with your aim).
    this.player.eye(this.eye);
    this.boardPoint(0, 0, this.center, 0);
    const aim = this.game.aimAt();
    const follow = this.game.phase === 'you' ? 0.25 : 0;
    this.boardPoint(aim.x * follow + 0.35, aim.y * follow - 0.1, this.tmp, 0);
    camera.position.copy(this.eye);
    camera.position.y -= 0.05;
    camera.lookAt(this.tmp);
    // The dart in your hand: cocked back by your ear, then gone when you let it fly.
    const since = this.t - this.throwAt;
    const ready = this.game.phase === 'you' && since > 0.45;
    this.inHand.visible = ready || since < 0.12;
    const push = since < 0.12 ? since / 0.12 : 0;
    this.inHand.position.set(0.11 - push * 0.08, -0.085 + push * 0.04, -0.3 - push * 0.35);
    this.inHand.rotation.set(0.12, -0.18, 0);
    this.inHand.rotateY(Math.PI);
    const fov = 30;
    if (camera.fov !== fov) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
    void dt;
  }

  hud(): StageHud {
    const g = this.game;
    const over = g.phase === 'over';
    return {
      score: `${g.score.you} – ${g.score.them}`,
      status: over
        ? ''
        : `Round ${g.round}/3 · ${g.phase === 'you' ? `your dart ${g.thrown + 1} of 3` : `${g.opponent.split(' ')[0]} is up`}`,
      banner: over
        ? g.won
          ? { text: 'YOU WIN!', color: '#9dff6a' }
          : g.score.you === g.score.them
            ? { text: 'TIE!', color: '#ffd400' }
            : { text: 'YOU LOSE', color: '#ff8a8a' }
        : null,
    };
  }

  end(): void {
    const { setup, player } = this;
    if (this.camera) {
      this.camera.fov = this.baseFov;
      this.camera.updateProjectionMatrix();
    }
    for (const f of this.flying) f.mesh.removeFromParent();
    this.clearDarts();
    this.reticle.removeFromParent();
    this.reticle.geometry.dispose();
    this.inHand.removeFromParent();
    setup.chalk.material = this.chalkOld;
    this.chalkTex.dispose();
    if (setup.rival) setup.rival.hold = null;
    player.scripted = false;
    player.viewHidden = false;
    player.syncTransform();
  }
}
